import React, {useState, useEffect, useMemo} from 'react';
import {Container, Card, Spinner, Alert, Table, Button} from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState
} from '@tanstack/react-table';
import {Class} from '../../context/types';
import {getClasses} from '../../services/api';
import ClassFormModal from './ClassFormModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Header from '../checkin/Header';


const ClassManagement: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sorting state - default sort by name
  const [sorting, setSorting] = useState<SortingState>([
    {id: 'name', desc: false}
  ]);

  // Modal state management
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);

  // Fetch classes function (used on mount and after CRUD operations)
  const fetchClasses = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getClasses();
      setClasses(data);
    } catch (err: any) {
      console.error('Error fetching classes:', err);

      let errorMessage: string;
      if (err.response) {
        // Server responded with error
        if (err.response.status === 403) {
          errorMessage = 'You do not have permission to view classes.';
        } else {
          errorMessage = err.response.data?.detail || 'Failed to fetch classes.';
        }
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = 'Failed to fetch classes.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
  }, []);

  // Modal handlers
  const handleOpenCreateModal = () => {
    setSelectedClass(null);
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (classItem: Class) => {
    setSelectedClass(classItem);
    setShowEditModal(true);
  };

  const handleOpenDeleteModal = (classItem: Class) => {
    setSelectedClass(classItem);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedClass(null);
  };

  const handleRefreshClasses = () => {
    fetchClasses();
  };

  // Define columns for the table
  const columnHelper = createColumnHelper<Class>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: info => info.getValue() || '-'
      }),
      columnHelper.accessor('tag', {
        header: 'Tag',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('day', {
        header: 'Day',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('time', {
        header: 'Time',
        cell: info => info.getValue()
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (props) => (
          <div className="d-flex flex-fill">
            <Button
              variant="success"
              onClick={() => handleOpenEditModal(props.row.original)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              onClick={() => handleOpenDeleteModal(props.row.original)}
            >
              Delete
            </Button>
          </div>
        )
      })
    ],
    [columnHelper]
  );

  // Create table instance
  const table = useReactTable({
    data: classes,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  return (
    <div style={{overflowX: 'hidden'}}>
      <Header title={'Class Management'}/>
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button variant="success" onClick={handleOpenCreateModal}>
            Add New Class
          </Button>
        </div>

        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="my-3">
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Card>
            <Card.Body>
              <Card.Title>Classes</Card.Title>
              <div style={{maxHeight: '600px', overflow: 'auto'}}>
                <Table striped bordered hover variant="dark">
                  <thead style={{position: 'sticky', top: 0, zIndex: 1}}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          style={{
                            cursor: header.column.id !== 'actions' ? 'pointer' : 'default',
                            userSelect: 'none'
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.id !== 'actions' && (
                            <>
                              {{
                                asc: ' ▲',
                                desc: ' ▼'
                              }[header.column.getIsSorted() as string] ?? null}
                            </>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                  </thead>
                  <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No classes found. Click "Add New Class" to create one.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Modals */}
        <ClassFormModal
          show={showCreateModal}
          mode="create"
          onClose={handleCloseModals}
          onSave={handleRefreshClasses}
        />

        <ClassFormModal
          show={showEditModal}
          mode="edit"
          classToEdit={selectedClass}
          onClose={handleCloseModals}
          onSave={handleRefreshClasses}
        />

        <DeleteConfirmationModal
          show={showDeleteModal}
          classToDelete={selectedClass}
          onClose={handleCloseModals}
          onDelete={handleRefreshClasses}
        />
      </Container>
    </div>
  );
};

export default ClassManagement;
